import type {
  PropertyFilterOperator,
  PropertyFilterOperatorExtended,
} from '@cloudscape-design/collection-hooks';
import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import dayjs from 'dayjs';

import { DateTimeForm } from '@/components/date-time-filter/DateTimeForm';
import { getDateRangeFormat, toLocalDate } from '@/utils/dateUtils';

import { convertDateRangeValues } from '../../pages/dashboards/widgets/filterHelpers';
import { RelativeDateTimeForm } from './RelativeDateTimeForm';

const dateFilterOperator = (
  operator: PropertyFilterOperator
): PropertyFilterOperatorExtended<null | string> => ({
  operator,
  form: DateTimeForm,
  format: toLocalDate,
  match: (itemValue, tokenValue) => {
    const item = itemValue as string;
    const token = tokenValue as string;

    const itemDate = dayjs(item);

    switch (operator) {
      case '<':
        return itemDate.isBefore(token, 'day');
      case '<=':
        return itemDate.isBefore(token, 'day') || itemDate.isSame(token, 'day');
      case '>':
        return itemDate.isAfter(token, 'day');
      case '>=':
        return itemDate.isAfter(token, 'day') || itemDate.isSame(token, 'day');
      case ':':
      case '=':
        return itemDate.isSame(token, 'day');
      case '!:':
      case '!=':
        return !itemDate.isSame(token, 'day');
      default:
        return false;
    }
  },
});

const dateFilterOperators = (
  operators: PropertyFilterOperator[]
): PropertyFilterOperatorExtended<null | string>[] =>
  operators.map(dateFilterOperator);

export const defaultDateFilterOperators = dateFilterOperators([
  '<=',
  '>=',
  '>',
  '<',
  '=',
]);

export const dateRangeFilterOperators: PropertyFilterOperatorExtended<
  null | string
>[] = [
  {
    operator: '=',
    form: RelativeDateTimeForm,
    format: getDateRangeFormat,
    match: (itemValue, tokenValue: null | string) => {
      const tItemValue = itemValue as string;
      const tTokenValue =
        (tokenValue as unknown as DateRangePickerProps.Value) || null;

      const token = convertDateRangeValues(tTokenValue);
      const itemDate = dayjs(tItemValue);

      const isSameDay =
        itemDate.isSame(token.startDate, 'day') ||
        itemDate.isSame(token.endDate, 'day');

      if (isSameDay) {
        return true;
      }

      const isAfterStartDate = itemDate.isAfter(token.startDate, 'day');
      const isBeforeEndDate = itemDate.isBefore(token.endDate, 'day');

      return isAfterStartDate && isBeforeEndDate;
    },
  },
];
